import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {Box} from 'platform/foundation';
import styled from 'styled-components';

import {KeyboardEvent, ReactNode} from 'react';

import {isNil} from 'ramda';

import {FOCUSABLE_ELEMENTS_SELECTOR, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR} from '../constants/AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR';

const GAP_STEPS = 1;
const VIEWPORT_PADDING_STEPS = 2;
// Floor for the height cap. Below it the popover would show a header and nothing else, so on a very
// short viewport it runs past the bottom edge and the page scrolls instead.
const MIN_MAX_HEIGHT_STEPS = 60;

interface AiComposerPopoverProps extends RequiredTestIdProps {
  label: string;
  /** Called on an outside click, on focus leaving the popover, and never on Escape. */
  onClose: () => void;
  children: ReactNode;
}

const Floating = styled.div`
  z-index: ${({theme}) => theme.zIndices.DROPDOWN_COMPONENT};
  outline: 0;
`;

export function AiComposerPopover(props: AiComposerPopoverProps) {
  const {refs, floatingStyles, context} = useFloating({
    open: true,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        props.onClose();
      }
    },
    placement: 'bottom-end',
    strategy: 'fixed',
    // No flip and no shift along the vertical axis: on a short screen they moved the popover above
    // the editor, over its own link, the recipient and the subject (seen at 1024×768). The popover
    // stays below the editor and `size` caps its height to the room left, so the suggestion list
    // scrolls instead. Shift only keeps it inside the viewport horizontally.
    middleware: [
      offset(({elements}) => stepsToPixels(elements.floating, GAP_STEPS)),
      shift({padding: stepsToPixels(document.documentElement, VIEWPORT_PADDING_STEPS)}),
      size({
        padding: stepsToPixels(document.documentElement, VIEWPORT_PADDING_STEPS),
        apply({availableHeight, elements}) {
          elements.floating.style.setProperty(
            AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR,
            `${Math.max(availableHeight, stepsToPixels(elements.floating, MIN_MAX_HEIGHT_STEPS))}px`
          );
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context, {escapeKey: false});
  const role = useRole(context, {role: 'dialog'});
  const {getReferenceProps, getFloatingProps} = useInteractions([dismiss, role]);

  return (
    <Box position="absolute" top={0} right={0}>
      <span ref={refs.setReference} {...getReferenceProps()} />
      <FloatingPortal>
        <FloatingFocusManager context={context} modal={false} initialFocus={-1} returnFocus={false}>
          <Floating
            ref={refs.setFloating}
            style={floatingStyles} // eslint-disable-line react/forbid-component-props -- Pre-existing violation
            aria-label={props.label}
            {...getFloatingProps({onKeyDown: keepTabInside})}
            data-testid={suffixTestId('popover', props)}
          >
            {props.children}
          </Floating>
        </FloatingFocusManager>
      </FloatingPortal>
    </Box>
  );
}

/**
 * Tab cycles inside the popover. Past the last suggestion it used to leave for the page, and the
 * close button could not be reached by keyboard (seen in review). A modal focus trap would also
 * hide the page and its notifications from screen readers, so the loop is kept by hand.
 */
const keepTabInside = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Tab') {
    return;
  }
  const focusable = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR)
  ).filter((element) => element.tabIndex >= 0 && !element.matches(':disabled'));
  const first = focusable.at(0);
  const last = focusable.at(-1);
  const active = event.currentTarget.ownerDocument.activeElement;
  if (isNil(first) || isNil(last)) {
    return;
  }
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

/** floating-ui measures in pixels; the theme's space step is a CSS variable on the root element. */
const stepsToPixels = (element: Element, steps: number) => {
  const root = element.ownerDocument.documentElement;
  const step = Number.parseFloat(getComputedStyle(root).getPropertyValue('--eag-space-step'));
  const fontSize = Number.parseFloat(getComputedStyle(root).fontSize);
  const stepPixels = Number.isFinite(step) && Number.isFinite(fontSize) ? step * fontSize : 4;
  return steps * stepPixels;
};
